import React, { useEffect, useCallback, useState } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { doc, setDoc } from "firebase/firestore";
import { GoogleGenerativeAI } from '@google/generative-ai';
import { Stack, CircularProgress, IconButton } from '@mui/material';
import MobileStepper from '@mui/material/MobileStepper';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';

import LinearProgress from '@mui/material/LinearProgress';
import ChatInput from '../components/ChatInput';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import {db} from '../firebase-config'
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

export default function SignUp({uid, email, setProfile}) {
  const [chatSession, setChatSession] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [lastmessage, setLastMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [initializing, setInitializing] = useState(true);
  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);
  const [user, setUser] = useState({
    name: '',
    gender: '',
    weight: '',
    height: '',
    age: '',
    goals: ''
  })

  const [substep, setSubstep] = useState(0);
  const [mounted, setMounted] = useState(true);

  useEffect(() => {
    return () => {
      setMounted(false);
    };
  }, []);

  const handleApiError = (error) => {
    console.error('API call failed:', error);
    setError('Gemini API Error. Please try again.');
    setLoading(false);
    setInitializing(false);
  };


  const initializeGemini = useCallback(async () => {
    if (!mounted) return;
    setInitializing(true);
    setLoading(true);
    
    const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    const genAI = new GoogleGenerativeAI(apiKey)
    const model = genAI.getGenerativeModel({
      model: 'gemini-2.0-flash',
      systemInstruction: `
      You are a battle hardened warrior sensei. Skilled in martial arts now guiding others on their fitness and health journey. 
      
      Here is how this interaction will go:

      user: hello  
      
      you: Prime the mind of the user with something inspirational. welcome them and ask for their name.

      user: Responds with their name.

      you: ask about their health and fitness goals.

      user: responds with their goals.

      You: ask them about their gender, age, height and weight to craft a diet plan to achieve their goals. 
      
      user: responds with their gender, age, height and weight.

      You: * Provide a max 100-word response with a wholistic diet plan tailored to the user's goals and metrics. Also mention macros here. *
      
      Finally, ask what their current workout routine looks like.

      User: the user will respond with their workout routine.

      You: respond in this format:
      * max 100 words response. Use 80/20 principal to recommend a training routine for the week and what a daily routine should look like to achieve their goals. *
      * give user dislaimer that this is not for the faint of heart and they can either choose to change their life forever or do nothing about it. *
      `,
    });

    const generationConfig = {
      temperature: 1,
      topP: 0.95,
      topK: 64,
      maxOutputTokens: 8192,
      responseMimeType: 'text/plain',
    };

    const chatSession = model.startChat({
      generationConfig,
      history: [],
    });

    try{
      const result = await chatSession.sendMessage('hello');
      setLastMessage(result.response.text())
      setChatHistory((prevHistory) => [
          ...prevHistory,
          { role: 'you', parts: [{ text: result.response.text() }] },
      ]);
      setChatSession(chatSession);
    }catch(e){
      handleApiError(e);
    } finally {
      setLoading(false);
      setInitializing(false);
    }
  }, [mounted]);

  useEffect(() => {
    initializeGemini();
  }, [initializeGemini]);

  const userRegistration = async (payload) => {
    if (!mounted) return;
    console.log('user registered', payload);
    try {
      await setDoc(doc(db, "users", payload.uid), payload);
      setProfile(payload);
      return true;
    } catch (err) {
      console.error("Error during registration:", err);
      throw err;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Disable multiple submissions by setting loading state
    if (loading) return;
    setLoading(true);
    
    let payload = {
      ...user,
      uid: uid,
      mealPlan:{}
    }
    
    console.log('final payload', payload);
    
    try {
      await userRegistration(payload);
      console.log('Registration successful! Redirecting to your dashboard...');
      // Show success message
      setLastMessage("Registration successful! Redirecting to your dashboard...");
    } catch (error) {
      console.error("Registration failed:", error);
      setError("Registration failed. Please try again.");
      setLoading(false);
    }
  };

  const handleNameInput = async (name) => {
    if (!mounted) return;
    setUser({...user, name: name})
    setLoading(true)
    try{
      const result = await chatSession.sendMessage(name);
      setChatHistory((prevHistory) => [
          ...prevHistory,
          { role: 'user', parts: [{ text: name }] },
          { role: 'you', parts: [{ text: result.response.text() }]},
      ]);
      setLastMessage(result.response.text())
      setStep(1)
    }catch(e){
      handleApiError(e);
    }finally{
      setLoading(false)
    }
  }

  const handleGoalsInput = async (goal) => {
    setUser({...user, goals: goal})
    setLoading(true)
    try{
      const result = await chatSession.sendMessage(goal);
      setChatHistory((prevHistory) => [
          ...prevHistory,
          { role: 'user', parts: [{ text: goal }] },
          { role: 'you', parts: [{ text: result.response.text() }]},
      ]);
      setLastMessage(result.response.text())
      setStep(2)
    }catch(e){
      handleApiError(e);
    }finally{
      setLoading(false)
    }
  }

  const handleStep2Submission = async (height) => {
    setLoading(true);
    try {
      // Get text response from the model
      const textResult = await chatSession.sendMessage(
        `I am a ${user.age} year old ${user.gender}. Height is ${height} and weight is ${user.weight}. Please provide a diet plan tailored to my goals and metrics. use 40(carbs), 30(protiens), 30(fats) split`
      );
      
      const recommendedDailyDiet = textResult.response.text().trim();

      // Set up a new model instance for JSON output
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      const genAI = new GoogleGenerativeAI(apiKey);
      const jsonModel = genAI.getGenerativeModel({
        model: "gemini-2.0-flash",
        generationConfig: { responseMimeType: "application/json" }
      });

      // Prepare the JSON prompt
      const jsonPrompt = `
        Based on the following information, provide daily macro targets. use 40(carbs), 30(protiens), 30(fats) split:
        Age: ${user.age}
        Gender: ${user.gender}
        Height: ${height}
        Weight: ${user.weight}
        Goals: ${user.goals}

        Respond with a JSON object in this format:
        {
          "targetCalories": number,
          "targetProteins": number,
          "targetFat": number,
          "targetCarbs": number
        }
      `;

      // Get JSON response
      const jsonResult = await jsonModel.generateContent(jsonPrompt);
      const dailyMacrosJSON = JSON.parse(jsonResult.response.text());

      setUser({
        ...user,
        height: height,
        recommendedDiet: recommendedDailyDiet,
        targetMacros: dailyMacrosJSON
      });
  
      setChatHistory((prevHistory) => [
        ...prevHistory,
        { role: 'user', parts: [{ text: `I am a ${user.age} year old ${user.gender}. Height is ${height} and weight is ${user.weight}` }] },
        { role: 'you', parts: [{ text: recommendedDailyDiet }] },
      ]);
      setLastMessage(recommendedDailyDiet);
      setStep(3);
    } catch (e) {
      handleApiError(e);
    }finally{
      setLoading(false)
    }
  };

  const handleStep3Submission = async (currentTrainingRoutine) => {
    setLoading(true);
    try {
      // Get text response from the model
      const textResult = await chatSession.sendMessage(currentTrainingRoutine);

      setChatHistory((prevHistory) => [
        ...prevHistory,
        { role: 'user', parts: [{ text: currentTrainingRoutine }] },
        { role: 'you', parts: [{ text: textResult.response.text() }]},
      ]);
      setLastMessage(textResult.response.text());

      // Set up a new model instance for JSON output
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      const genAI = new GoogleGenerativeAI(apiKey);
      const jsonModel = genAI.getGenerativeModel({
        model: "gemini-2.0-flash",
        generationConfig: { responseMimeType: "application/json" }
      });

      // Prepare the JSON prompt
      const jsonPrompt = `
        Based on the following user information and their current training routine, provide a recommended training plan:
        User Info: ${JSON.stringify(user)}
        Current Training Routine: ${currentTrainingRoutine}
        AI Response: ${textResult.response.text()}

        Respond with a JSON object in this format:
        {
          "recommendedTraining": {
            "dailyRoutine": "string describing a holistic daily plan to help the user build consistent habits for both physical and mental improvement.",
            "weeklyRoutine": {
              "monday": "string describing training focus or rest day",
              "tuesday": "string describing training focus or rest day",
              "wednesday": "string describing training focus or rest day",
              "thursday": "string describing training focus or rest day",
              "friday": "string describing training focus or rest day",
              "saturday": "string describing training focus or rest day",
              "sunday": "string describing training focus or rest day"
            }
          }
        }
      `;

      // Get JSON response
      const jsonResult = await jsonModel.generateContent(jsonPrompt);
      const recommendedTrainingJSON = JSON.parse(jsonResult.response.text());

      console.log({
        ...user,
        recommendedTraining: recommendedTrainingJSON.recommendedTraining
      })
      setUser({
        ...user,
        recommendedTraining: recommendedTrainingJSON.recommendedTraining
      });

      setStep(4);
    } catch (e) {
      handleApiError(e);
    }finally{
      setLoading(false)
    }
  };

  const handleBack = () => {
    if (step === 2) {
      if (substep > 0) {
        setSubstep(substep - 1);
        
        if (substep === 1) {
          setUser({...user, gender: ''});
        } else if (substep === 2) {
          setUser({...user, age: ''});
        } else if (substep === 3) {
          setUser({...user, weight: ''});
        }
      } else {
        setStep(1);
        setSubstep(0);
      }
    } else if (step > 0) {
      setStep(step - 1);
      
      if (step === 3) {
        setSubstep(3);
        setUser({...user, height: ''});
      }
    }
  };

  useEffect(() => {
    if (step === 2) {
      if (user.gender && !user.age) {
        setSubstep(1);
      } else if (user.gender && user.age && !user.weight) {
        setSubstep(2);
      } else if (user.gender && user.age && user.weight && !user.height) {
        setSubstep(3);
      }
    }
  }, [user.gender, user.age, user.weight, user.height, step]);

  const renderStep = () => {
    if (initializing) {
      return null;
    }
    
    if(step===0){
      return(
        <ChatInput 
          onSendMessage={handleNameInput} 
          inputLabel='Your Name'
        />
      )
    }if(step===1){
      return(
        <>
          <ChatInput 
            onSendMessage={handleGoalsInput} 
            inputLabel='Your Goals'
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={handleBack}
              size="small"
            >
              Back
            </Button>
          </Box>
        </>
      )  
    }if(step===2){
      if(user.gender?.length===0){
        return(
          <>
            <ChatInput 
              onSendMessage={(gender)=>setUser({...user, gender: gender})} 
              inputLabel='Your Gender'
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
              <Button 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBack}
                size="small"
              >
                Back
              </Button>
            </Box>
          </>
        )
      }else if(user.age?.length===0){
        return(
          <>
            <ChatInput 
              onSendMessage={(age)=>setUser({...user, age: age})} 
              inputLabel='Your Age'
              type='age'
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
              <Button 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBack}
                size="small"
              >
                Back
              </Button>
            </Box>
          </>
        )
      }else if(user.weight?.length===0){
        return(
          <>
            <ChatInput 
              onSendMessage={(weight)=>setUser({...user, weight: weight})} 
              inputLabel='Your Weight in lbs'
              type='weight'
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
              <Button 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBack}
                size="small"
              >
                Back
              </Button>
            </Box>
          </>
        )
      }else if(user.height?.length===0){
        return(
          <>
            <ChatInput 
              onSendMessage={handleStep2Submission} 
              inputLabel='Your Height'
              type='height'
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
              <Button 
                startIcon={<ArrowBackIcon />} 
                onClick={handleBack}
                size="small"
              >
                Back
              </Button>
            </Box>
          </>
        )
      }
    }else if(step===3){
      return(
        <>
          <ChatInput 
            onSendMessage={handleStep3Submission} 
            inputLabel='explain your current workout routine'
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={handleBack}
              size="small"
            >
              Back
            </Button>
          </Box>
        </>
      )
    }else{
      return(
        <>
          <Button onClick={handleSubmit} variant="contained">Lets Begin !</Button>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
            <Button 
              startIcon={<ArrowBackIcon />} 
              onClick={handleBack}
              size="small"
            >
              Back
            </Button>
          </Box>
        </>
      )
    }
  }

  return(
    <Container component="main" maxWidth="xs" >
      <Stack spacing={2}>
        <img width={'100%'} src="./sensei.png" alt="Taekwondo Image" />
        {
          loading ?
          <Stack spacing={2} alignItems="center">
            <LinearProgress color="success" sx={{ width: '100%' }} />
            <Typography variant="body2" color="text.secondary">
              {initializing ? "Connecting with your Sensei..." : "Your Sensei is thinking..."}
            </Typography>
          </Stack>
          :
          <Stack spacing={5}>
            {
              step===3? 
              <Card sx={{ minWidth: 275 }}>
                <CardContent>
                  <Typography sx={{ fontSize: 14 }} color="text.primary" gutterBottom>
                    Macros
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    Target Calories: {user.targetMacros.targetCalories} kcal
                    <br/>
                    Target Proteins: {user.targetMacros.targetProteins} grams
                    <br/>
                    Target Fat: {user.targetMacros.targetFat} grams
                    <br />
                    Target Carbs: {user.targetMacros.targetCarbs} grams
                  </Typography>
                </CardContent>
              </Card>
              :
              null
            }
            <Box
              sx={{
                maxHeight: 150,
                overflowY: 'auto',
                padding: 2,
                borderRadius: 1,
                '&::-webkit-scrollbar': {
                  width: '0.4em'
                },
                '&::-webkit-scrollbar-track': {
                  boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
                  webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,.1)',
                  outline: '1px solid slategrey'
                }
              }}
            >
              <Typography align='center' component="p" variant="p">
                {lastmessage}
              </Typography>
            </Box>
            {renderStep()}
            <MobileStepper
              variant="dots"
              steps={5}
              position="static"
              activeStep={step}
              sx={{ width: '100%', justifyContent: 'center' }}
            />
          </Stack>
        }
        {error && (
          <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
            <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Snackbar>
        )}
      </Stack>
    </Container>
  )
}
